import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import apiService from "../services/apiService";
import Modal from "../components/Modal";
import { toast } from 'react-toastify';
const SubscriptionBundle = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null); // per-plan loading

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await apiService.get("/stripe/plans");
        setPlans(res || []);
      } catch (err) {
        setError("Failed to load plans.");
      }
    };
    fetchPlans();
  }, []);

 const handleCheckout = async (plan) => {
  if (!isAuthenticated()) {
    setShowLogin(true);
    return;
  }

  setLoadingPlan(plan._id);
  setError("");

  try {
    const res = await apiService.post("/stripe/create-checkout", {
      planId: plan._id,
    });

    if (res.url) {
      // Optional: show different toast based on mode
      if (res.mode === "one_time") {
        toast.info("One-time access selected – pay once for fixed period");
      } else {
        toast.info("Recurring subscription selected – will auto-renew");
      }
      window.location.href = res.url;
    } else {
      setError("Failed to start checkout.");
    }
  } catch (err) {
    console.error(err);
    setError(err.response?.error || "Payment failed to start.");
  } finally {
    setLoadingPlan(null);
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f3eb] via-[#f3faf5] to-[#e8f7ee] py-16 px-4">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-extrabold text-[#1e3c28] mb-6 tracking-tight">
            Choose Your Access Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            One-time payment • Instant Access • Secure Stripe Checkout
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="max-w-2xl mx-auto mb-10 p-6 bg-red-100 border-2 border-red-400 rounded-xl text-red-800 text-center font-medium">
            {error}
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan._id}
              className="relative bg-white rounded-3xl shadow-lg hover:shadow-2xl p-10 text-center transition-all duration-300 hover:-translate-y-1 border border-gray-100"
            >
              {/* Yearly badge */}
        {(plan.interval === "yearly" || plan.interval === "3years") && (
    <div className="absolute -top-4 z-10">
      <span className="bg-[#1e5c33] text-white px-6 py-2 rounded-full font-semibold shadow-lg text-sm md:text-base">
        {plan.interval === "yearly"
          ? "BEST VALUE • SAVE 50%"
          : "BEST VALUE • SAVE UP TO 70%"}
      </span>
    </div>
  )}
              <h3 className="text-4xl font-bold text-[#1e3c28] mb-6">{plan.name}</h3>

              <div className="text-7xl font-extrabold text-[#1e5c33] mb-4">
                £{plan.price}
              </div>

             <p className="text-xl text-gray-600 mb-8">
                {(() => {
                  switch (plan.interval) {
                    case "monthly":
                      return "30 days access";
                    case "yearly":
                      return "12 months access";
                    case "2years":
                      return "24 months (2 years) access";
                    case "3years":
                      return "36 months (3 years) access";
                    default:
                      return `${plan.interval} access`;
                  }
                })()}
              </p>
              <div
                className="text-left text-gray-700 mb-8 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: plan.description }}
              />

              {/* Payment Button */}
              <button
                onClick={() => handleCheckout(plan)}
                disabled={loadingPlan === plan._id}
                className="w-full bg-[#1e5c33] hover:bg-[#174927] text-white font-bold py-5 rounded-2xl text-xl shadow-xl transition-all hover:scale-105 disabled:opacity-60"
              >
                {loadingPlan === plan._id ? "Preparing..." : `Pay £${plan.price} Securely`}
              </button>

              <div className="bg-gray-50 px-10 py-6 mt-6 text-center text-sm text-gray-500 rounded-xl border">
                Secured by Stripe • Apple Pay • Google Pay • Virtual Cards
              </div>
            </div>
          ))}
        </div>

        {/* Login Modal */}
        <Modal
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
          title="Login Required"
        >
          <p className="text-center text-lg mb-8 text-gray-700">
            You need to be logged in to make a purchase.
          </p>

          <div className="flex justify-center gap-6">
            <button
              onClick={() => navigate("/login")}
              className="px-10 py-4 bg-[#1e5c33] hover:bg-[#174927] text-white rounded-xl font-semibold"
            >
              Login
            </button>

            <button
              onClick={() => navigate("/signup")}
              className="px-10 py-4 bg-[#2e7c49] hover:bg-[#25663d] text-white rounded-xl font-semibold"
            >
              Sign Up
            </button>
          </div>
        </Modal>

      </div>
    </div>
  );
};

export default SubscriptionBundle;
